"use client"

import { PhoneBlockContent, Theme, defaultTheme } from "@/types"
import { useState, useEffect } from "react"
import { BlockImage } from "../BlockImage"
import { RichTextRenderer } from "../RichTextRenderer"

interface PhoneBlockProps {
  content: PhoneBlockContent
  onAnswer?: (answer: string) => void
  answer?: string
  theme?: Theme | null
  previewMode?: "desktop" | "mobile"
}

// Phone validation regex - supports various formats
const PHONE_REGEX = /^[\d\s\-\(\)\+\.]{10,}$/

// Clean phone number for validation (remove non-digits)
const cleanPhoneNumber = (phone: string): string => {
  return phone.replace(/\D/g, '')
}

// Validate phone has at least 10 digits
const validatePhone = (phone: string): boolean => {
  const cleaned = cleanPhoneNumber(phone)
  return cleaned.length >= 10 && cleaned.length <= 15
}

export function PhoneBlock({ content, onAnswer, answer: initialAnswer, theme = null }: PhoneBlockProps) {
  const [phone, setPhone] = useState("")
  const [isValid, setIsValid] = useState(true)
  const [hasBlurred, setHasBlurred] = useState(false)
  
  // Use provided theme or fallback to default
  const activeTheme = theme || defaultTheme

  // Initialize with existing answer if provided
  useEffect(() => {
    if (initialAnswer !== undefined) {
      setPhone(initialAnswer)
      setIsValid(validatePhone(initialAnswer))
    }
  }, [initialAnswer])

  const handlePhoneChange = (value: string) => {
    setPhone(value)
    const valid = value.trim() === "" || validatePhone(value)
    setIsValid(valid)
    
    // Only call onAnswer if phone is valid
    if (valid && value.trim() !== "") {
      onAnswer?.(value.trim())
    } else {
      // Clear answer if invalid
      onAnswer?.("")
    }
  }

  const handleBlur = () => {
    setHasBlurred(true)
    if (phone.trim() !== "") {
      const valid = validatePhone(phone)
      setIsValid(valid)
    }
  }

  return (
    <div>
      <h3 
        className="text-2xl font-semibold mb-3 text-center"
        style={{ 
          color: activeTheme.colors.text,
          fontFamily: activeTheme.fonts.heading
        }}
      >
        <RichTextRenderer content={content.question} />
      </h3>

      <div className="max-w-2xl mx-auto">
        <input
          type="tel"
          value={phone}
          onChange={(e) => handlePhoneChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={content.placeholder || "(555) 123-4567"}
          className={`w-full px-6 py-4 text-lg border-2 rounded-xl shadow-sm focus:outline-none focus-visible:outline-none transition-all ${
            hasBlurred && !isValid ? 'border-red-500' : 'border-gray-200'
          }`}
          style={{
            boxShadow: 'none',
          } as React.CSSProperties}
          onFocus={(e) => {
            const focusColor = isValid ? activeTheme.colors.primary : '#ef4444'
            e.target.style.borderColor = focusColor
            e.target.style.boxShadow = `0 0 0 2px ${focusColor}40`
          }}
          onBlur={(e) => {
            e.target.style.borderColor = hasBlurred && !isValid ? '#ef4444' : '#e5e7eb'
            e.target.style.boxShadow = 'none'
          }}
        />
        {hasBlurred && !isValid && phone.trim() !== "" && (
          <p className="mt-2 text-sm text-red-500 text-center">
            Please enter a valid phone number (at least 10 digits)
          </p>
        )}
      </div>
    </div>
  )
}

